/**
 * Client-side subdomain validation utility
 * This mirrors the server-side validation logic for consistency
 */

/**
 * List of reserved subdomains that should not be allowed
 */
export const RESERVED_SUBDOMAINS = [
  // System names
  'admin', 'api', 'app', 'mail', 'smtp', 'www', 'support',
  'dev', 'test', 'staging', 'production', 'sandbox',
  
  // Common services
  'webmail', 'cpanel', 'whm', 'billing', 'ftp', 'blog',
  'forum', 'community', 'help', 'status', 'cdn', 'docs',
  
  // Security related
  'auth', 'login', 'signup', 'sso', 'secure', 'security', 'account',
  'forgot-password', 'reset-password', 'verify-email',
  
  // Infrastructure related
  'server', 'ns1', 'ns2', 'dns', 'gateway', 'host',
  'dashboard', 'portal', 'panel', 'internal', 'localhost',
  
  // Common web files
  'robots', 'sitemap', 'manifest', 'favicon',
  
  // Reserved words for future use
  'settings', 'profile', 'about', 'contact', 'privacy', 
  'terms', 'faq', 'static', 'assets', 'css', 'js', 'images',
  
  // Application-specific paths
  'menu', 'preview', 'dishes', 'translations', 'translation-queue'
];

/**
 * Profanity filter is simplified here - in production you would 
 * use a more comprehensive list or a service
 */
const PROFANITY_LIST = [
  // This is just a minimal example - replace with a proper profanity filter
  'inappropriate1', 'inappropriate2'
];

/**
 * Validates a subdomain with comprehensive security checks
 * @param {string} subdomain - The subdomain to validate
 * @returns {object} Result object with valid status, message, and normalized subdomain if valid
 */
export const validateSubdomain = (subdomain) => {
  // Null/undefined check
  if (!subdomain) {
    return {
      valid: false,
      message: 'Subdomain is required'
    };
  }
  
  // Normalize the input
  const normalizedSubdomain = subdomain.trim().toLowerCase();
  
  // Empty check after normalization
  if (normalizedSubdomain.length === 0) {
    return {
      valid: false,
      message: 'Subdomain cannot be empty'
    };
  }
  
  // Length check
  if (normalizedSubdomain.length < 3) {
    return {
      valid: false,
      message: 'Subdomain must be at least 3 characters long'
    };
  }
  
  if (normalizedSubdomain.length > 63) {
    return {
      valid: false,
      message: 'Subdomain must be less than 64 characters long'
    };
  }
  
  // Basic format check
  const basicFormatRegex = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
  if (!basicFormatRegex.test(normalizedSubdomain)) {
    return {
      valid: false,
      message: 'Subdomain must use only lowercase letters, numbers, and hyphens, and cannot start or end with a hyphen'
    };
  }
  
  // Check for consecutive hyphens (can look suspicious or be used for visual phishing)
  if (normalizedSubdomain.includes('--')) {
    return {
      valid: false,
      message: 'Subdomain cannot contain consecutive hyphens'
    };
  }
  
  // Check against reserved list
  if (RESERVED_SUBDOMAINS.includes(normalizedSubdomain)) {
    return {
      valid: false,
      message: 'This subdomain name is reserved and cannot be used'
    };
  }
  
  // Check against profanity list
  if (PROFANITY_LIST.some(word => normalizedSubdomain.includes(word))) {
    return {
      valid: false,
      message: 'This subdomain contains inappropriate content'
    };
  }
  
  // Additional check for homoglyphs and confusable characters
  if (/[óôöòœøōõő]/.test(normalizedSubdomain)) {
    return {
      valid: false, 
      message: 'Subdomain contains characters that could be confused with Latin characters'
    };
  }
  
  // All checks passed!
  return {
    valid: true,
    normalizedSubdomain,
    message: 'Subdomain is valid'
  };
};

/**
 * Sanitizes a subdomain for safe display in HTML
 * @param {string} subdomain - The subdomain to sanitize
 * @returns {string} Sanitized subdomain safe for HTML display
 */
export const sanitizeSubdomainForDisplay = (subdomain) => {
  if (!subdomain) return '';
  
  // Basic HTML escaping
  return subdomain
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Create a named object for the default export
const subdomainValidator = {
  validateSubdomain,
  sanitizeSubdomainForDisplay,
  RESERVED_SUBDOMAINS
};

export default subdomainValidator; 